import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import api from "@/api";
import { MatchRow } from "@/components/MatchesStrip";
import { useI18n } from "@/i18n";
import { useSeo } from "@/seo";

const PERIODS = [0, 7, 14, 30];
const PER_PAGE = 10;

export default function MatchesPage() {
  const { t, lang } = useI18n();
  const [facets, setFacets] = useState({ leagues: [], clubs: [] });
  const [filters, setFilters] = useState({ league: "", club: "", days: 0 });
  const [data, setData] = useState({ upcoming: [], recent: [] });
  const [pages, setPages] = useState({ upcoming: 1, recent: 1 });
  const [loading, setLoading] = useState(true);

  useSeo(t("matches.page_title"), t("matches.page_desc"), "/matches");

  useEffect(() => {
    api.get("/matches/facets").then((r) => setFacets(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const params = { limit: 60 };
    if (filters.league) params.league = filters.league;
    if (filters.club) params.club = filters.club;
    if (filters.days) params.days = filters.days;
    setLoading(true);
    Promise.all([
      api.get("/matches", { params: { ...params, kind: "upcoming" } }),
      api.get("/matches", { params: { ...params, kind: "recent" } }),
    ])
      .then(([u, r]) => setData({ upcoming: u.data.matches, recent: r.data.matches }))
      .catch(() => setData({ upcoming: [], recent: [] }))
      .finally(() => setLoading(false));
    setPages({ upcoming: 1, recent: 1 });
  }, [filters]);

  const sections = [
    { kind: "upcoming", title: t("matches.upcoming"), items: data.upcoming, testid: "matches-page-upcoming" },
    { kind: "recent", title: t("matches.recent"), items: data.recent, testid: "matches-page-recent" },
  ];

  return (
    <div className="pt-8" data-testid="matches-page">
      <h1 className="font-display text-4xl font-bold uppercase tracking-tight text-white sm:text-5xl">
        {t("matches.page_title")}
      </h1>
      <p className="mt-2 max-w-2xl text-sm text-slate-400 md:text-base">{t("matches.page_desc")}</p>

      <div className="mt-6 flex flex-wrap gap-3" data-testid="matches-filters">
        <Select label={t("matches.filter_league")} value={filters.league} testid="filter-league"
          onChange={(v) => setFilters({ ...filters, league: v })}
          options={[["", t("matches.filter_all")], ...facets.leagues.map((l) => [l, l])]} />
        <Select label={t("matches.filter_club")} value={filters.club} testid="filter-club"
          onChange={(v) => setFilters({ ...filters, club: v })}
          options={[["", t("matches.filter_all")], ...facets.clubs.map((c) => [c, c])]} />
        <Select label={t("matches.filter_period")} value={String(filters.days)} testid="filter-period"
          onChange={(v) => setFilters({ ...filters, days: Number(v) })}
          options={PERIODS.map((d) => [String(d), d ? t("matches.period_days", { n: d }) : t("matches.filter_all")])} />
      </div>

      <p className="mt-3 font-mono2 text-[10px] uppercase tracking-wider text-slate-600">{t("matches.page_hint")}</p>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {sections.map((s) => {
          const page = pages[s.kind];
          const pageCount = Math.max(1, Math.ceil(s.items.length / PER_PAGE));
          const slice = s.items.slice((page - 1) * PER_PAGE, page * PER_PAGE);
          return (
            <section key={s.kind} data-testid={s.testid}>
              <h2 className="mb-3 font-display text-base font-bold uppercase tracking-tight text-white md:text-lg">
                {s.title} <span className="font-mono2 text-xs text-slate-500">({s.items.length})</span>
              </h2>
              {s.items.length === 0 ? (
                <p className="rounded-lg border border-[#1F293D] bg-[#0B0E14]/70 px-3 py-6 text-center font-mono2 text-[11px] uppercase tracking-wider text-slate-500">
                  {loading ? "…" : t("matches.empty")}
                </p>
              ) : (
                <>
                  <div className="space-y-2">
                    {slice.map((m) => <MatchRow key={m.match_id} m={m} kind={s.kind} t={t} lang={lang} full />)}
                  </div>
                  {pageCount > 1 && (
                    <div className="mt-3 flex items-center justify-between gap-2" data-testid={`pager-${s.kind}`}>
                      <button
                        type="button"
                        disabled={page <= 1}
                        onClick={() => setPages({ ...pages, [s.kind]: page - 1 })}
                        data-testid={`pager-${s.kind}-prev`}
                        className="rounded-md border border-[#1F293D] px-2.5 py-1.5 font-mono2 text-[10px] uppercase tracking-wider text-slate-300 transition-colors hover:border-[#00FF87]/50 hover:text-[#00FF87] disabled:opacity-30"
                      >
                        ← {t("matches.prev")}
                      </button>
                      <span className="font-mono2 text-[10px] uppercase tracking-wider text-slate-500" data-testid={`pager-${s.kind}-state`}>
                        {t("matches.page_of", { page, total: pageCount })}
                      </span>
                      <button
                        type="button"
                        disabled={page >= pageCount}
                        onClick={() => setPages({ ...pages, [s.kind]: page + 1 })}
                        data-testid={`pager-${s.kind}-next`}
                        className="rounded-md border border-[#1F293D] px-2.5 py-1.5 font-mono2 text-[10px] uppercase tracking-wider text-slate-300 transition-colors hover:border-[#00FF87]/50 hover:text-[#00FF87] disabled:opacity-30"
                      >
                        {t("matches.next")} →
                      </button>
                    </div>
                  )}
                </>
              )}
            </section>
          );
        })}
      </div>

      <Link to="/" className="mt-8 inline-block font-mono2 text-[11px] uppercase tracking-wider text-[#00FF87] hover:underline" data-testid="matches-back-home">
        ← {t("nav.watchlist")}
      </Link>
    </div>
  );
}

const Select = ({ label, value, onChange, options, testid }) => (
  <label className="flex flex-col gap-1">
    <span className="font-mono2 text-[9px] uppercase tracking-[.08em] text-slate-500">{label}</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="min-w-[9rem] rounded-md border border-[#1F293D] bg-[#0B0E14] px-2.5 py-2 font-mono2 text-[11px] uppercase tracking-wider text-slate-200 focus:border-[#00FF87]/60 focus:outline-none"
    >
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </label>
);
